import { motion } from "framer-motion";
import { BarChart3, Heart, Eye, Users, HeartHandshake, Globe2, Newspaper } from "lucide-react";

/**
 * Iter-27.73 — Vue détaillée des statistiques (onglet "Statistiques").
 *
 * MVP simple : reprend les KPI du dashboard avec plus de détail visuel et un
 * breakdown par catégorie. Évolutivité prévue : courbes temporelles + export CSV.
 */
export default function AssoAnalytics({ stats }) {
    if (!stats) {
        return (
            <div className="h-64 rounded-3xl bg-white/3 border border-white/10 animate-pulse" />
        );
    }

    const { counts = {}, reactions = {}, story_views_30d = 0 } = stats;
    const totalContent = (counts.gratitude || 0) + (counts.impact || 0) + (counts.news || 0);
    const reactionsRatio =
        totalContent > 0 ? (reactions.total / totalContent).toFixed(1) : "0";

    return (
        <div className="space-y-6" data-testid="asso-analytics">
            {/* Header */}
            <div>
                <p className="text-[10px] font-thin tracking-[0.25em] uppercase text-shariti-yellow mb-0.5">
                    Statistiques
                </p>
                <h2 className="text-xl sm:text-2xl font-extrabold text-white">
                    Performance de ton espace
                </h2>
                <p className="text-xs sm:text-sm text-white/60 font-light mt-1">
                    Mesure l'engagement de tes ambassadeurs sur tes contenus.
                </p>
            </div>

            {/* Big KPIs */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <BigKpi
                    label="Réactions totales"
                    value={reactions.total || 0}
                    sub={`${reactionsRatio} en moyenne par contenu`}
                    icon={Heart}
                    color="#EE722E"
                    testId="big-kpi-reactions"
                />
                <BigKpi
                    label="Vues Stories (30j)"
                    value={story_views_30d}
                    sub="Stories ouvertes par les ambassadeurs"
                    icon={Eye}
                    color="#FCDB53"
                    testId="big-kpi-views"
                />
                <BigKpi
                    label="Ambassadeurs"
                    value={counts.ambassadors || 0}
                    sub="Affectés à ta mission"
                    icon={Users}
                    color="#a78bfa"
                    testId="big-kpi-ambassadors"
                />
            </div>

            {/* Breakdown réactions par emoji */}
            <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className="rounded-3xl p-5 sm:p-6 bg-white/3 border border-white/10"
            >
                <h3 className="text-base font-extrabold text-white mb-4">
                    Détail des réactions reçues
                </h3>
                {reactions.total > 0 ? (
                    <div className="space-y-2">
                        {Object.entries(reactions.by_emoji || {})
                            .sort((a, b) => b[1] - a[1])
                            .map(([emoji, count]) => {
                                const pct = (count / reactions.total) * 100;
                                return (
                                    <div
                                        key={emoji}
                                        className="flex items-center gap-3"
                                        data-testid={`reaction-bar-${emoji}`}
                                    >
                                        <span className="text-2xl shrink-0 w-8 text-center">
                                            {emoji}
                                        </span>
                                        <div className="flex-1">
                                            <div className="flex items-center justify-between text-xs font-bold mb-1">
                                                <span className="text-white/70 tabular-nums">
                                                    {count}
                                                </span>
                                                <span className="text-white/45 tabular-nums">
                                                    {pct.toFixed(0)}%
                                                </span>
                                            </div>
                                            <div className="h-2 rounded-full bg-white/5 overflow-hidden">
                                                <motion.div
                                                    initial={{ width: 0 }}
                                                    animate={{ width: `${pct}%` }}
                                                    transition={{ duration: 0.8, ease: "easeOut" }}
                                                    className="h-full bg-gradient-to-r from-shariti-orange to-shariti-yellow"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                    </div>
                ) : (
                    <p className="text-sm text-white/45 italic">
                        Aucune réaction reçue pour le moment.
                    </p>
                )}
            </motion.div>

            {/* Breakdown par type de contenu */}
            <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="rounded-3xl p-5 sm:p-6 bg-white/3 border border-white/10"
            >
                <h3 className="text-base font-extrabold text-white mb-4">
                    Répartition de tes contenus
                </h3>
                <div className="grid grid-cols-3 gap-3">
                    <ContentSlice
                        label="Remerciements"
                        value={counts.gratitude || 0}
                        icon={HeartHandshake}
                        color="#FCDB53"
                    />
                    <ContentSlice
                        label="Histoires"
                        value={counts.impact || 0}
                        icon={Globe2}
                        color="#EE722E"
                    />
                    <ContentSlice
                        label="Actualités"
                        value={counts.news || 0}
                        icon={Newspaper}
                        color="#7dd3fc"
                    />
                </div>
            </motion.div>
        </div>
    );
}


function BigKpi({ label, value, sub, icon: Icon, color, testId }) {
    return (
        <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            className="rounded-3xl p-5 sm:p-6 bg-white/3 border border-white/10"
            data-testid={testId}
        >
            <div className="flex items-start justify-between mb-4">
                <div
                    className="w-11 h-11 rounded-2xl flex items-center justify-center"
                    style={{
                        background: `${color}1A`,
                        border: `1px solid ${color}40`,
                    }}
                >
                    <Icon className="w-5 h-5" style={{ color }} />
                </div>
            </div>
            <p className="text-3xl sm:text-4xl font-extrabold text-white tabular-nums tracking-tight">
                {value}
            </p>
            <p className="text-[11px] font-bold text-white/65 uppercase tracking-wider mt-1">
                {label}
            </p>
            {sub && <p className="text-[11px] text-white/40 font-light mt-1.5">{sub}</p>}
        </motion.div>
    );
}


function ContentSlice({ label, value, icon: Icon, color }) {
    return (
        <div
            className="text-center py-4 rounded-2xl border"
            style={{
                background: `${color}10`,
                borderColor: `${color}30`,
            }}
        >
            <Icon className="w-5 h-5 mx-auto mb-1.5" style={{ color }} />
            <p className="text-2xl font-extrabold text-white tabular-nums">{value}</p>
            <p className="text-[10px] font-bold text-white/55 uppercase tracking-wider mt-0.5">
                {label}
            </p>
        </div>
    );
}
